import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DoneIcon from '@mui/icons-material/Done';
import DeleteIcon from '@mui/icons-material/Delete';
import notificationService from '../services/notificationService';

const formatTime = (d) => {
  try {
    const date = d instanceof Date ? d : new Date(d);
    const now = new Date();
    const diffMs = Math.max(0, now - date);
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  } catch {
    return '';
  }
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const unreadCount = useMemo(() => notificationService.getUnreadCount(items), [items]);

  async function load() {
    try {
      setLoading(true);
      const notifs = await notificationService.fetchNotifications({ limit: 15 });
      setItems(notifs);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // initial fetch
    load();
    // periodic refresh while dashboard is open
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = () => {
    setOpen(true);
    load();
  };
  const handleClose = () => setOpen(false);

  const handleView = async (n) => {
    try { await notificationService.markAsRead(n.id); } catch {}
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
    handleClose();
    if (n.type === 'teacher_feedback' && n.testId) {
      navigate(`/detailed-results/${n.testId}`);
    }
  };

  const handleMarkRead = async (n) => {
    try { await notificationService.markAsRead(n.id); } catch {}
    setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
  };

  const handleDelete = async (n) => {
    try { await notificationService.deleteNotification(n.id); } catch {}
    setItems(prev => prev.filter(x => x.id !== n.id));
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="notifications">
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <Typography variant="h6">Notifications</Typography>
            <Typography variant="caption" color="text.secondary">{unreadCount} unread</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading…</Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">No notifications</Typography>
            </Box>
          ) : (
            <List dense sx={{ py: 0, maxHeight: '70vh', overflowY: 'auto' }}>
              {items.map((n) => (
                <React.Fragment key={n.id}>
                  <ListItem alignItems="flex-start" sx={{ opacity: n.read ? 0.7 : 1, pr: 18 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ fontWeight: n.read ? 400 : 600 }}>
                          {n.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">{n.message}</Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          {formatTime(n.createdAt)}
                        </Typography>
                        <IconButton size="small" title="View" onClick={() => handleView(n)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {!n.read && (
                          <IconButton size="small" title="Mark as read" onClick={() => handleMarkRead(n)}>
                            <DoneIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" title="Delete" onClick={() => handleDelete(n)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={load}>Refresh</Button>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
